'use client'
import { useMemo, useState } from 'react'
import ErrorBoundary from '@error-boundary'
import dynamic from 'next/dynamic'

import type {
  AcfImageObject,
  BuildingBlockKeyFactsRepeaterItemListRepeaterProps,
  BuildingBlockKeyFactsRepeaterItemMapRepeaterProps,
  BuildingBlockKeyFactsRepeaterItemProps,
  BuildingBlockKeyFactsRepeaterItemSentenceRepeaterProps,
  BuildingBlockKeyFactsRepeaterItemTableRepeaterProps,
  BuildingBlockPostType,
  RoundtripKeyFactsRepeaterItemListRepeaterProps,
  RoundtripKeyFactsRepeaterItemMapRepeaterProps,
  RoundtripKeyFactsRepeaterItemProps,
  RoundtripKeyFactsRepeaterItemSentenceRepeaterProps,
  RoundtripKeyFactsRepeaterItemTableRepeaterProps,
  RoundtripPostType,
} from '@typings'
import {
  RaContainer,
  RaImage,
  RaRichText,
  RaTypography,
} from '@components/core/index.core'
import { RaButtonAddToCart } from '@components/features/RaButtonAddToCart/RaButtonAddToCart'
import { RaKeyfactWithoutIcon } from '@components/features/RaKeyfactWithoutIcon/RaKeyfactWithoutIcon'
import { Loop } from '@utilities/helpers/core.helpers'

import styles from './ra-keyfacts.module.scss'
import { RaButtonAddToWishlist } from '../RaButtonAddToWishlist/RaButtonAddToWishlist'

const RaStepmap = dynamic(() =>
  import('@components/features/RaStepmap/RaStepmap').then(mod => mod.RaStepmap)
)

const RaTabs = dynamic(() =>
  import('@components/features/RaTabs/RaTabs').then(mod => mod.RaTabs)
)

type Tab =
  | BuildingBlockKeyFactsRepeaterItemProps
  | RoundtripKeyFactsRepeaterItemProps
  | string

interface RaKeyFactsProps {
  tabs: Tab[]
  image?: AcfImageObject
  item?: BuildingBlockPostType | RoundtripPostType
  mapTabTitle: string
}

type TableRepeater =
  | BuildingBlockKeyFactsRepeaterItemTableRepeaterProps
  | RoundtripKeyFactsRepeaterItemTableRepeaterProps
type SentenceRepeater =
  | BuildingBlockKeyFactsRepeaterItemSentenceRepeaterProps
  | RoundtripKeyFactsRepeaterItemSentenceRepeaterProps
type ListRepeater =
  | BuildingBlockKeyFactsRepeaterItemListRepeaterProps
  | RoundtripKeyFactsRepeaterItemListRepeaterProps
type MapRepeater =
  | BuildingBlockKeyFactsRepeaterItemMapRepeaterProps
  | RoundtripKeyFactsRepeaterItemMapRepeaterProps

export const RaKeyFacts = ({
  tabs,
  image,
  item,
  mapTabTitle,
}: RaKeyFactsProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0)

  const activeTabPanel = useMemo(() => {
    return tabs[activeTabIndex]
  }, [tabs, activeTabIndex])

  const tableRepeater = useMemo(() => {
    if (
      !activeTabPanel ||
      typeof activeTabPanel === 'string' ||
      activeTabPanel.output_type !== 'table'
    )
      return null
    const keyfacts = activeTabPanel.output_type_table_repeater
    if (!keyfacts || keyfacts.length === 0) return null
    return (
      <ul data-name="table-repeater">
        {Loop(
          keyfacts.filter(item => !item.remove_from_detail),
          (item: TableRepeater, i: number) => (
            <RaKeyfactWithoutIcon
              item={item}
              key={i}
            />
          )
        )}
      </ul>
    )
  }, [activeTabPanel])

  const senenceRepeater = useMemo(() => {
    if (
      !activeTabPanel ||
      typeof activeTabPanel === 'string' ||
      activeTabPanel.output_type !== 'sentence'
    )
      return null
    const keyfacts = activeTabPanel.output_type_sentence_repeater
    if (!keyfacts || keyfacts.length === 0) return null
    return (
      <ul data-name="sentence-repeater">
        {Loop(keyfacts, (item: SentenceRepeater, i: number) => {
          return (
            <li key={i}>
              <RaTypography component="span">{item.text}</RaTypography>
            </li>
          )
        })}
      </ul>
    )
  }, [activeTabPanel])

  const listRepeater = useMemo(() => {
    if (
      !activeTabPanel ||
      typeof activeTabPanel === 'string' ||
      activeTabPanel.output_type !== 'list'
    )
      return null
    const keyfacts = activeTabPanel.output_type_list_repeater
    if (!keyfacts || keyfacts.length === 0) return null
    return (
      <ul data-name="list-repeater">
        {Loop(keyfacts, (item: ListRepeater, i: number) => {
          return (
            <li key={i}>
              <RaRichText
                decorateListsWithBullets
                content={item.text}
              />
            </li>
          )
        })}
      </ul>
    )
  }, [activeTabPanel])

  const mapRepeater = useMemo(() => {
    if (!activeTabPanel || typeof activeTabPanel === 'string') return null
    const keyfacts = activeTabPanel.output_type_map_repeater
    if (!keyfacts || keyfacts.length === 0) return null
    return (
      <ul data-name="map-repeater">
        {Loop(keyfacts, (item: MapRepeater, i: number) => {
          return (
            <li key={i}>
              <RaTypography component="span">{item.small_map}</RaTypography>
              <RaTypography component="span">{item.big_map}</RaTypography>
            </li>
          )
        })}
      </ul>
    )
  }, [activeTabPanel])

  const mapTab = useMemo(() => {
    if (!activeTabPanel || typeof activeTabPanel !== 'string') return null
    return (
      <div className={styles['ra-keyfacts__stepmap']}>
        <RaStepmap
          stepmapScript={activeTabPanel}
          index={'1'}
        />
      </div>
    )
  }, [activeTabPanel])

  const additionalTextContent = useMemo(() => {
    if (
      !activeTabPanel ||
      typeof activeTabPanel === 'string' ||
      !activeTabPanel.additional_text_content
    )
      return null
    return (
      <div data-name="additional-text-content">
        <RaRichText content={activeTabPanel.additional_text_content} />
      </div>
    )
  }, [activeTabPanel])

  const tabListJSON: any[] = useMemo(() => {
    return Loop(tabs, (tab, i: number) => {
      if (!tab) return null
      return {
        value: i,
        text: typeof tab === 'string' ? mapTabTitle : tab.description,
        isSelected: activeTabIndex === i,
      }
    })
  }, [tabs])

  const onTabSelect = (i: number) => {
    setActiveTabIndex(i)
  }

  return (
    <ErrorBoundary>
      <div className={styles['ra-keyfacts']}>
        <RaTabs
          tabsData={tabListJSON}
          activeTabIndex={activeTabIndex}
          onTabSelect={onTabSelect}
          mobileDirection="horizontal"
        />
        <RaContainer>
          <div className={styles['ra-keyfacts__tab-panel']}>
            {activeTabPanel && (
              <div>
                {tableRepeater}
                {senenceRepeater}
                {listRepeater}
                {mapRepeater}
                {additionalTextContent}
                {mapTab}
              </div>
            )}
            <div className={styles['ra-keyfacts__sidebar']}>
              <div className={styles['ra-keyfacts__image']}>
                {image && (
                  <RaImage
                    image={image}
                    polaroid
                    rotate="right"
                  />
                )}
              </div>
              <div className={styles['ra-keyfacts__buttons']}>
                <RaButtonAddToCart item={item} />
                <RaButtonAddToWishlist
                  product={item}
                  variant="outlined"
                />
              </div>
            </div>
          </div>
        </RaContainer>
      </div>
    </ErrorBoundary>
  )
}
